import { useQuery } from '@tanstack/react-query'
import { notificationsFeedApi } from '@/api/notifications-feed'
import type { FeedItem } from '@/api/notifications-feed'

export type AlertRow = FeedItem

export interface AlertEnvGroup {
  environment: string
  items: AlertRow[]
}

export interface AlertClientGroup {
  client: string
  environments: AlertEnvGroup[]
  total: number
}

export function useAlertsFeed() {
  return useQuery({
    queryKey: ['alerts-feed'],
    staleTime: 30_000,
    queryFn: async (): Promise<AlertClientGroup[]> => {
      // Usa o filtro server-side por source=zabbix via FeedFilters.sources.
      // O filtro client-side garante consistência mesmo se o backend retornar extras.
      // limit máx do backend é 100 (Query le=100); 200 retornava 422 e o feed
      // ficava VAZIO p/ todos. 100 cobre o feed agrupado recente do MVP.
      const res = await notificationsFeedApi.list({ sources: ['zabbix'], limit: 100 })
      const items: AlertRow[] = res.items.filter((i) => i.source === 'zabbix')

      const byClient = new Map<string, Map<string, AlertRow[]>>()
      for (const it of items) {
        const c = (it.source_meta?.client as string) || 'Sem cliente'
        const e = (it.source_meta?.environment as string) || '—'
        if (!byClient.has(c)) byClient.set(c, new Map())
        const envs = byClient.get(c)!
        if (!envs.has(e)) envs.set(e, [])
        envs.get(e)!.push(it)
      }

      return [...byClient.entries()]
        .map(([client, envs]) => ({
          client,
          total: [...envs.values()].reduce((n, a) => n + a.length, 0),
          environments: [...envs.entries()].map(([environment, envItems]) => ({
            environment,
            items: envItems,
          })),
        }))
        .sort((a, b) => a.client.localeCompare(b.client))
    },
  })
}
